import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  useAdminCheck,
  useAdminGetStats,
  useAdminGetExtendedStats,
  useAdminListLeagues,
  useAdminListPendingLeagueMatches,
  useAdminListPendingGlobalMatches,
  useAdminListPendingScheduledMatches,
  useAdminListAllMatches,
  useAdminListClubs,
  getAdminCheckQueryKey,
  getAdminGetStatsQueryKey,
  getAdminGetExtendedStatsQueryKey,
  getAdminListLeaguesQueryKey,
  getAdminListPendingLeagueMatchesQueryKey,
  getAdminListPendingGlobalMatchesQueryKey,
  getAdminListPendingScheduledMatchesQueryKey,
  getAdminListAllMatchesQueryKey,
  getAdminListClubsQueryKey,
} from "@workspace/api-client-react";
import type {
  AdminListAllMatchesStatus,
  AdminListAllMatchesType,
  AdminListClubsVerified,
  Club,
} from "@workspace/api-client-react";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Trophy,
  Users,
  Building2,
  ClipboardCheck,
  BarChart3,
  ArrowLeft,
  Check,
  X,
  Clock,
  Mail,
  Phone,
  MapPin,
  MessageSquare,
  Shield,
  RotateCcw,
  Ban,
  UserCheck,
  Download,
  Bell,
  Settings,
  Trash2,
  Eye,
  FileText,
  Activity,
  TrendingUp,
  UserPlus,
  ShieldCheck,
  ShieldX,
  Pencil,
  GitMerge,
  Plus,
  Upload,
  Globe,
} from "lucide-react";
import type { User, ClubAccessRequest, GlobalMatch, AdminAuditLog, SystemSetting } from "@shared/schema";
import type {
  AdminStats,
  ExtendedAdminStats,
  AdminLeague,
  PendingLeagueMatch,
  AdminMatch,
} from "@workspace/api-client-react";
import { EditPendingMatchDialog } from "@/components/EditPendingMatchDialog";

type ParsedCsvRow = { name: string; country: string; city: string | null; verified: boolean };
type ParseCsvResult = {
  items: ParsedCsvRow[];
  invalidRows: { line: number; reason: string }[];
  totalDataRows: number;
};

function parseClubsCsv(rawText: string): ParseCsvResult {
  // Strip UTF-8 BOM if present so the header parses correctly.
  const text = rawText.replace(/^\ufeff/, "");
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else { inQuotes = false; }
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ',') { cur.push(field); field = ""; }
      else if (ch === '\n' || ch === '\r') {
        if (field !== "" || cur.length > 0) { cur.push(field); rows.push(cur); cur = []; field = ""; }
        if (ch === '\r' && text[i + 1] === '\n') i++;
      } else {
        field += ch;
      }
    }
  }
  if (field !== "" || cur.length > 0) { cur.push(field); rows.push(cur); }
  if (rows.length === 0) return { items: [], invalidRows: [], totalDataRows: 0 };
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const idxName = header.indexOf("name");
  const idxCountry = header.indexOf("country");
  const idxCity = header.indexOf("city");
  const idxVerified = header.indexOf("verified");
  if (idxName === -1 || idxCountry === -1) {
    throw new Error("El CSV debe tener columnas 'name' y 'country'");
  }
  const items: ParsedCsvRow[] = [];
  const invalidRows: { line: number; reason: string }[] = [];
  let totalDataRows = 0;
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (row.every((c) => c.trim() === "")) continue;
    totalDataRows++;
    const lineNo = r + 1; // 1-indexed including header
    const name = (row[idxName] ?? "").trim();
    const country = (row[idxCountry] ?? "").trim().toUpperCase();
    if (name.length < 2) {
      invalidRows.push({ line: lineNo, reason: "nombre vacío o muy corto" });
      continue;
    }
    if (!/^[A-Z]{2}$/.test(country)) {
      invalidRows.push({ line: lineNo, reason: `country '${country || "(vacío)"}' no es ISO-2` });
      continue;
    }
    const city = idxCity >= 0 ? (row[idxCity] ?? "").trim() : "";
    const verifiedRaw = idxVerified >= 0 ? (row[idxVerified] ?? "").trim().toLowerCase() : "true";
    const verified = !["false", "0", "no", "n", ""].includes(verifiedRaw);
    items.push({ name, country, city: city || null, verified });
  }
  return { items, invalidRows, totalDataRows };
}

export function AdminPanel() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("usuarios");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [matchStatusFilter, setMatchStatusFilter] = useState<"all" | AdminListAllMatchesStatus>("all");
  const [matchTypeFilter, setMatchTypeFilter] = useState<"all" | AdminListAllMatchesType>("all");
  const [selectedRequest, setSelectedRequest] = useState<ClubAccessRequest | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; action: string; userId?: string; leagueId?: string; matchId?: string; matchType?: string }>({ open: false, action: "" });
  const [notificationDialog, setNotificationDialog] = useState(false);
  const [notificationData, setNotificationData] = useState({ userId: "", title: "", message: "" });
  // Edit pending match (league or global). When set, renders the
  // shared EditPendingMatchDialog. Reuses the same component as the
  // player-facing flows so admins get the context switcher too.
  // Clubs directory state
  const [clubsTab, setClubsTab] = useState<AdminListClubsVerified>("pending");
  const [clubSearch, setClubSearch] = useState("");
  const [clubCountry, setClubCountry] = useState("DO");
  const [editingClub, setEditingClub] = useState<Club | null>(null);
  const [clubEditDraft, setClubEditDraft] = useState<{ name: string; country: string; city: string }>({ name: "", country: "", city: "" });
  const [mergeClub, setMergeClub] = useState<Club | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState<string>("");
  const [deleteClubId, setDeleteClubId] = useState<string | null>(null);
  const [createClubOpen, setCreateClubOpen] = useState(false);
  const [createClubDraft, setCreateClubDraft] = useState<{ name: string; country: string; city: string; verified: boolean }>({ name: "", country: "DO", city: "", verified: true });
  const [bulkClubsOpen, setBulkClubsOpen] = useState(false);
  const [bulkClubsFile, setBulkClubsFile] = useState<File | null>(null);
  const [bulkClubsResult, setBulkClubsResult] = useState<{ inserted: number; skipped: number; total: number; errors: { row: number; message: string }[]; invalidRows?: { line: number; reason: string }[]; csvDataRows?: number } | null>(null);

  const [editPendingMatch, setEditPendingMatch] = useState<{
    id: string;
    type: "league" | "global";
    leagueId?: string;
    score: unknown;
    winner: number;
    team1Player1Id?: string;
    team1Player2Id?: string | null;
    team2Player1Id?: string;
    team2Player2Id?: string | null;
    playedAt?: string | Date | null;
  } | null>(null);

  const { data: adminCheck, isLoading: checkingAdmin } = useAdminCheck({
    query: { queryKey: getAdminCheckQueryKey(), retry: false },
  });

  const isAdmin = adminCheck?.isAdmin === true;

  const { data: stats } = useAdminGetStats({
    query: { queryKey: getAdminGetStatsQueryKey(), enabled: isAdmin },
  });

  const { data: extendedStats } = useAdminGetExtendedStats({
    query: { queryKey: getAdminGetExtendedStatsQueryKey(), enabled: isAdmin && activeTab === "analiticas" },
  });

  const { data: clubRequests, isLoading: loadingRequests } = useQuery<ClubAccessRequest[]>({
    queryKey: ["/api/admin/club-requests", statusFilter],
    queryFn: async () => {
      const url = statusFilter !== "all"
        ? `/api/admin/club-requests?status=${statusFilter}`
        : "/api/admin/club-requests";
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch");
      return response.json();
    },
    enabled: isAdmin,
  });

  const { data: users, isLoading: loadingUsers } = useQuery<User[]>({
    queryKey: ["/api/admin/users"],
    enabled: isAdmin,
  });

  const { data: leagues, isLoading: loadingLeagues } = useAdminListLeagues({
    query: { queryKey: getAdminListLeaguesQueryKey(), enabled: isAdmin && activeTab === "ligas" },
  });

  const { data: pendingLeagueMatches } = useAdminListPendingLeagueMatches({
    query: { queryKey: getAdminListPendingLeagueMatchesQueryKey(), enabled: isAdmin && activeTab === "partidos" },
  });

  const { data: pendingGlobalMatches } = useAdminListPendingGlobalMatches({
    query: { queryKey: getAdminListPendingGlobalMatchesQueryKey(), enabled: isAdmin && activeTab === "partidos" },
  });

  const { data: pendingScheduledMatches } = useAdminListPendingScheduledMatches({
    query: { queryKey: getAdminListPendingScheduledMatchesQueryKey(), enabled: isAdmin && activeTab === "partidos" },
  });

  const allMatchesParams = {
    status: matchStatusFilter !== "all" ? matchStatusFilter : undefined,
    type: matchTypeFilter !== "all" ? matchTypeFilter : undefined,
  };
  const { data: allMatches, isLoading: loadingAllMatches } = useAdminListAllMatches(allMatchesParams, {
    query: { queryKey: getAdminListAllMatchesQueryKey(allMatchesParams), enabled: isAdmin && activeTab === "partidos" },
  });

  const { data: auditLogs } = useQuery<AdminAuditLog[]>({
    queryKey: ["/api/admin/audit-logs"],
    enabled: isAdmin && activeTab === "analiticas",
  });

  const { data: systemSettings } = useQuery<SystemSetting[]>({
    queryKey: ["/api/admin/settings"],
    enabled: isAdmin && activeTab === "configuracion",
  });

  const adminClubsParams = {
    verified: clubsTab,
    country: clubCountry || undefined,
    search: clubSearch.trim() || undefined,
  };
  const { data: adminClubs, isLoading: loadingClubs } = useAdminListClubs(adminClubsParams, {
    query: { queryKey: getAdminListClubsQueryKey(adminClubsParams), enabled: isAdmin && activeTab === "clubes" },
  });

  const createClubMutation = useMutation({
    mutationFn: async (data: { name: string; country: string; city: string | null; verified: boolean }) => {
      const response = await apiRequest("POST", "/api/admin/clubs", data);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Club creado" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/clubs"] });
      setCreateClubOpen(false);
      setCreateClubDraft({ name: "", country: clubCountry || "DO", city: "", verified: true });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "No se pudo crear el club", variant: "destructive" });
    },
  });

  const bulkClubsMutation = useMutation({
    mutationFn: async (items: Array<{ name: string; country: string; city: string | null; verified: boolean }>) => {
      const response = await apiRequest("POST", "/api/admin/clubs/bulk", { items });
      return response.json();
    },
    onSuccess: (result: any) => {
      setBulkClubsResult(result);
      toast({ title: "Carga completada", description: `Insertados: ${result.inserted} · Omitidos: ${result.skipped} · Errores: ${result.errors?.length ?? 0}` });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/clubs"] });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "No se pudo cargar el CSV", variant: "destructive" });
    },
  });

  const updateClubMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<{ name: string; country: string; city: string | null; verified: boolean }> }) => {
      const response = await apiRequest("PATCH", `/api/admin/clubs/${id}`, data);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Club actualizado" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/clubs"] });
      setEditingClub(null);
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "No se pudo actualizar el club", variant: "destructive" });
    },
  });

  const deleteClubMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("DELETE", `/api/admin/clubs/${id}`, {});
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Club eliminado" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/clubs"] });
      setDeleteClubId(null);
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "No se pudo eliminar el club", variant: "destructive" });
    },
  });

  const mergeClubMutation = useMutation({
    mutationFn: async ({ sourceId, targetId }: { sourceId: string; targetId: string }) => {
      const response = await apiRequest("POST", `/api/admin/clubs/${sourceId}/merge`, { targetId });
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Clubes fusionados" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/clubs"] });
      setMergeClub(null);
      setMergeTargetId("");
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e?.message || "No se pudo fusionar", variant: "destructive" });
    },
  });

  const updateRequestMutation = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      const response = await apiRequest("PATCH", `/api/admin/club-requests/${id}`, {
        status,
        reviewNotes: notes,
      });
      return response.json();
    },
    onSuccess: (_, variables) => {
      toast({
        title: variables.status === "approved" ? "Solicitud aprobada" : "Solicitud rechazada",
        description: "El estado de la solicitud ha sido actualizado.",
      });
      queryClient.invalidateQueries({ 
        predicate: (query) => 
          query.queryKey[0] === "/api/admin/club-requests" ||
          query.queryKey[0] === "/api/admin/stats"
      });
      setSelectedRequest(null);
      setReviewNotes("");
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo actualizar la solicitud.",
        variant: "destructive",
      });
    },
  });

  const userActionMutation = useMutation({
    mutationFn: async ({ userId, action }: { userId: string; action: string }) => {
      const response = await apiRequest("POST", `/api/admin/users/${userId}/${action}`, {});
      return response.json();
    },
    onSuccess: (_, variables) => {
      const actionMessages: Record<string, string> = {
        "block": "Usuario bloqueado",
        "unblock": "Usuario desbloqueado",
        "make-admin": "Rol de admin asignado",
        "remove-admin": "Rol de admin removido",
        "reset-stats": "Estadísticas reiniciadas",
      };
      toast({
        title: actionMessages[variables.action] || "Acción completada",
        description: "La operación se completó correctamente.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/extended-stats"] });
      setConfirmDialog({ open: false, action: "" });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "No se pudo completar la operación.",
        variant: "destructive",
      });
    },
  });

  const bulkActionMutation = useMutation({
    mutationFn: async ({ action, userIds }: { action: string; userIds: string[] }) => {
      const response = await apiRequest("POST", "/api/admin/bulk-action", { action, userIds });
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Acción masiva completada",
        description: `${data.affectedCount} usuarios afectados.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      setSelectedUsers([]);
      setBulkAction("");
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo completar la acción masiva.",
        variant: "destructive",
      });
    },
  });

  const deleteLeagueMutation = useMutation({
    mutationFn: async (leagueId: string) => {
      const response = await apiRequest("DELETE", `/api/admin/leagues/${leagueId}`, {});
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Liga eliminada",
        description: "La liga ha sido eliminada correctamente.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/leagues"] });
      setConfirmDialog({ open: false, action: "" });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo eliminar la liga.",
        variant: "destructive",
      });
    },
  });

  const adminScheduledMatchActionMutation = useMutation({
    mutationFn: async ({ matchId, action }: { matchId: string; action: "approve" | "reject" }) => {
      const response = await apiRequest("POST", `/api/scheduled-matches/${matchId}/${action}`);
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(err.message || "No se pudo procesar el partido");
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      toast({
        title: variables.action === "approve" ? "Resultado confirmado" : "Resultado rechazado",
        description: "La operación se completó correctamente.",
      });
      queryClient.invalidateQueries({ queryKey: getAdminListPendingScheduledMatchesQueryKey() });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "No se pudo completar la operación.", variant: "destructive" });
    },
  });

  const matchActionMutation = useMutation({
    mutationFn: async ({ matchId, matchType, action }: { matchId: string; matchType: string; action: string }) => {
      const method = action === "delete" ? "DELETE" : "POST";
      const url = action === "delete" 
        ? `/api/admin/matches/${matchType}/${matchId}`
        : `/api/admin/matches/${matchType}/${matchId}/${action}`;
      const response = await apiRequest(method, url, {});
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: "Error desconocido" }));
        throw new Error(errorData.message || "No se pudo completar la operación");
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      const actionMessages: Record<string, string> = {
        "approve": "Partido aprobado",
        "reject": "Partido rechazado",
        "delete": "Partido eliminado",
      };
      toast({
        title: actionMessages[variables.action] || "Acción completada",
        description: "La operación se completó correctamente.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/matches/league/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/matches/global/pending"] });
      queryClient.invalidateQueries({ predicate: (q) => (q.queryKey[0] as string)?.startsWith("/api/admin/matches/all") });
      setConfirmDialog({ open: false, action: "" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo completar la operación.",
        variant: "destructive",
      });
    },
  });

  const sendNotificationMutation = useMutation({
    mutationFn: async (data: { userId: string; title: string; message: string }) => {
      const response = await apiRequest("POST", "/api/admin/send-notification", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Notificación enviada",
        description: "La notificación ha sido enviada al usuario.",
      });
      setNotificationDialog(false);
      setNotificationData({ userId: "", title: "", message: "" });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo enviar la notificación.",
        variant: "destructive",
      });
    },
  });

  const [isRebuildingLevels, setIsRebuildingLevels] = useState(false);
  const [isRecalculatingRatings, setIsRecalculatingRatings] = useState(false);
  const [isRecalculatingNationalRanks, setIsRecalculatingNationalRanks] = useState(false);

  const handleRebuildLevelHistory = async () => {
    try {
      setIsRebuildingLevels(true);
      const response = await fetch("/api/admin/rebuild-level-history", { 
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Rebuild failed");
      toast({ 
        title: "Reconstrucción completada", 
        description: data.message || `${data.matchesProcessed} partidos procesados, ${data.entriesCreated} entradas creadas.`
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "No se pudo reconstruir el historial.", variant: "destructive" });
    } finally {
      setIsRebuildingLevels(false);
    }
  };

  const handleRecalculateRatings = async () => {
    try {
      setIsRecalculatingRatings(true);
      const response = await fetch("/api/admin/recalculate-all-ratings", { 
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Recalculation failed");
      toast({ 
        title: "Recálculo completado", 
        description: data.message || `${data.results?.length || 0} ligas procesadas.`
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/stats"] });
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "No se pudo recalcular ratings.", variant: "destructive" });
    } finally {
      setIsRecalculatingRatings(false);
    }
  };

  const handleRecalculateNationalRanks = async () => {
    try {
      setIsRecalculatingNationalRanks(true);
      const response = await fetch("/api/admin/national-ranks/recalculate", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Recalculation failed");
      toast({
        title: "Ranking nacional actualizado",
        description: data.message || "Rankings nacionales recalculados correctamente."
      });
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "No se pudo recalcular el ranking nacional.", variant: "destructive" });
    } finally {
      setIsRecalculatingNationalRanks(false);
    }
  };

  const handleExport = async (type: "users" | "leagues") => {
    try {
      const response = await fetch(`/api/admin/exports/${type}`, { credentials: "include" });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type === "users" ? "usuarios" : "ligas"}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({ title: "Exportación completada", description: `Archivo CSV de ${type === "users" ? "usuarios" : "ligas"} descargado.` });
    } catch {
      toast({ title: "Error", description: "No se pudo exportar los datos.", variant: "destructive" });
    }
  };

  const handleLogout = () => {
    import("@/lib/sessionPersistence").then(({ logout }) => logout());
  };

  const handleBackToApp = () => {
    window.location.href = "/";
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleBulkAction = () => {
    if (!bulkAction || selectedUsers.length === 0) return;
    bulkActionMutation.mutate({ action: bulkAction, userIds: selectedUsers });
  };

  if (checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Trophy className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  if (!adminCheck?.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <Shield className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Acceso Denegado</h2>
            <p className="text-muted-foreground mb-4">
              No tienes permisos de administrador para acceder a esta sección.
            </p>
            <Button onClick={handleBackToApp} data-testid="button-back-to-app">
              Volver a la App
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filteredRequests = clubRequests?.filter((request) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      request.clubName.toLowerCase().includes(term) ||
      request.contactName.toLowerCase().includes(term) ||
      request.email.toLowerCase().includes(term) ||
      request.city?.toLowerCase().includes(term)
    );
  });

  const filteredUsers = users?.filter((u) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      u.email?.toLowerCase().includes(term) ||
      u.firstName?.toLowerCase().includes(term) ||
      u.lastName?.toLowerCase().includes(term)
    );
  });

  const filteredLeagues = leagues?.filter((l) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      l.name.toLowerCase().includes(term) ||
      l.creatorName.toLowerCase().includes(term)
    );
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" />Pendiente</Badge>;
      case "approved":
        return <Badge className="gap-1 bg-green-600"><Check className="h-3 w-3" />Aprobada</Badge>;
      case "rejected":
        return <Badge variant="destructive" className="gap-1"><X className="h-3 w-3" />Rechazada</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <header 
        className="sticky top-0 z-40 flex items-center justify-between px-4 py-3 bg-background border-b border-border"
        style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
      >
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBackToApp} data-testid="button-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg tracking-tight">Panel Admin</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground hidden sm:block">
            {user?.firstName || user?.email}
          </span>
          <ThemeToggle />
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="button-logout">
            Salir
          </Button>
        </div>
      </header>

      <main className="p-4 max-w-7xl mx-auto safe-area-bottom">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Users className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.totalPlayers ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Jugadores</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Building2 className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.totalClubs ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Clubes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10">
                  <Clock className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.pendingRequests ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Check className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.approvedRequests ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Aprobadas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 w-full grid grid-cols-7">
            <TabsTrigger value="usuarios" className="gap-1 text-xs">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Usuarios</span>
            </TabsTrigger>
            <TabsTrigger value="ligas" className="gap-1 text-xs">
              <Trophy className="h-4 w-4" />
              <span className="hidden sm:inline">Ligas</span>
            </TabsTrigger>
            <TabsTrigger value="partidos" className="gap-1 text-xs">
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">Partidos</span>
            </TabsTrigger>
            <TabsTrigger value="solicitudes" className="gap-1 text-xs">
              <ClipboardCheck className="h-4 w-4" />
              <span className="hidden sm:inline">Solicitudes</span>
            </TabsTrigger>
            <TabsTrigger value="clubes" className="gap-1 text-xs" data-testid="tab-clubes">
              <Building2 className="h-4 w-4" />
              <span className="hidden sm:inline">Clubes</span>
            </TabsTrigger>
            <TabsTrigger value="analiticas" className="gap-1 text-xs">
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="configuracion" className="gap-1 text-xs">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Config</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="usuarios">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                  <div>
                    <CardTitle>Gestión de Usuarios</CardTitle>
                    <CardDescription>
                      Administra usuarios, roles y permisos
                    </CardDescription>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Input
                      placeholder="Buscar usuarios..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-48"
                      data-testid="input-search-users"
                    />
                    <Button variant="outline" size="sm" onClick={() => handleExport("users")} data-testid="button-export-users">
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                  </div>
                </div>
                {selectedUsers.length > 0 && (
                  <div className="flex items-center gap-2 mt-4 p-3 bg-muted rounded-lg">
                    <span className="text-sm">{selectedUsers.length} seleccionados</span>
                    <Select value={bulkAction} onValueChange={setBulkAction}>
                      <SelectTrigger className="w-40" data-testid="select-bulk-action">
                        <SelectValue placeholder="Acción masiva" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="block">Bloquear</SelectItem>
                        <SelectItem value="unblock">Desbloquear</SelectItem>
                        <SelectItem value="make-admin">Hacer Admin</SelectItem>
                        <SelectItem value="reset-stats">Reiniciar Stats</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={handleBulkAction} disabled={!bulkAction || bulkActionMutation.isPending} data-testid="button-bulk-apply">
                      Aplicar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedUsers([])} data-testid="button-clear-selection">
                      Limpiar
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {loadingUsers ? (
                  <div className="text-center py-8">
                    <Trophy className="h-8 w-8 text-primary mx-auto mb-2 animate-pulse" />
                    <p className="text-muted-foreground">Cargando usuarios...</p>
                  </div>
                ) : filteredUsers?.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-muted-foreground">No hay usuarios</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <Checkbox 
                              checked={selectedUsers.length === filteredUsers?.length && filteredUsers.length > 0}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedUsers(filteredUsers?.map(u => u.id) || []);
                                } else {
                                  setSelectedUsers([]);
                                }
                              }}
                            />
                          </TableHead>
                          <TableHead>Usuario</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead>Rol</TableHead>
                          <TableHead>Registro</TableHead>
                          <TableHead>Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers?.map((u) => (
                          <TableRow key={u.id} className={u.isBlocked ? "opacity-60" : ""}>
                            <TableCell>
                              <Checkbox 
                                checked={selectedUsers.includes(u.id)}
                                onCheckedChange={() => toggleUserSelection(u.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="font-medium">
                                {u.firstName || u.lastName
                                  ? `${u.firstName || ""} ${u.lastName || ""}`.trim()
                                  : "Sin nombre"}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono">
                                {u.id.substring(0, 8)}...
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{u.email || "-"}</TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <Badge variant={u.userType === "club" ? "default" : "secondary"} className="w-fit">
                                  {u.userType === "club" ? "Club" : "Jugador"}
                                </Badge>
                                {u.isBlocked && (
                                  <Badge variant="destructive" className="w-fit gap-1">
                                    <Ban className="h-3 w-3" />Bloqueado
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {u.isAdmin ? (
                                <Badge className="gap-1 bg-primary">
                                  <Shield className="h-3 w-3" />
                                  Admin
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">Usuario</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(u.createdAt)}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1 flex-wrap">
                                {u.isBlocked ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setConfirmDialog({ open: true, action: "unblock", userId: u.id })}
                                    data-testid={`button-unblock-${u.id}`}
                                  >
                                    <UserCheck className="h-3 w-3" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setConfirmDialog({ open: true, action: "block", userId: u.id })}
                                    data-testid={`button-block-${u.id}`}
                                  >
                                    <Ban className="h-3 w-3" />
                                  </Button>
                                )}
                                {u.isAdmin ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setConfirmDialog({ open: true, action: "remove-admin", userId: u.id })}
                                    disabled={u.id === user?.id}
                                    data-testid={`button-remove-admin-${u.id}`}
                                  >
                                    <ShieldX className="h-3 w-3" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setConfirmDialog({ open: true, action: "make-admin", userId: u.id })}
                                    data-testid={`button-make-admin-${u.id}`}
                                  >
                                    <ShieldCheck className="h-3 w-3" />
                                  </Button>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setConfirmDialog({ open: true, action: "reset-stats", userId: u.id })}
                                  data-testid={`button-reset-stats-${u.id}`}
                                >
                                  <RotateCcw className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setNotificationData({ ...notificationData, userId: u.id });
                                    setNotificationDialog(true);
                                  }}
                                  data-testid={`button-notify-${u.id}`}
                                >
                                  <Bell className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ligas">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                  <div>
                    <CardTitle>Gestión de Ligas</CardTitle>
                    <CardDescription>
                      Administra todas las ligas de la plataforma
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Buscar ligas..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-48"
                      data-testid="input-search-leagues"
                    />
                    <Button variant="outline" size="sm" onClick={() => handleExport("leagues")} data-testid="button-export-leagues">
                      <Download className="h-4 w-4 mr-1" />
                      CSV
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingLeagues ? (
                  <div className="text-center py-8">
                    <Trophy className="h-8 w-8 text-primary mx-auto mb-2 animate-pulse" />
                    <p className="text-muted-foreground">Cargando ligas...</p>
                  </div>
                ) : filteredLeagues?.length === 0 ? (
                  <div className="text-center py-8">
                    <Trophy className="h-12 w-12 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-muted-foreground">No hay ligas</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Liga</TableHead>
                          <TableHead>Creador</TableHead>
                          <TableHead>Miembros</TableHead>
                          <TableHead>Privada</TableHead>
                          <TableHead>Creada</TableHead>
                          <TableHead>Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredLeagues?.map((league) => (
                          <TableRow key={league.id}>
                            <TableCell>
                              <div className="font-medium">{league.name}</div>
                              <div className="text-xs text-muted-foreground font-mono">
                                {league.id.substring(0, 8)}...
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{league.creatorName}</TableCell>
                            <TableCell>
                              <Badge variant="outline">{league.memberCount} miembros</Badge>
                            </TableCell>
                            <TableCell>
                              {league.isPrivate ? (
                                <Badge variant="outline">Privada</Badge>
                              ) : (
                                <Badge className="bg-green-600">Pública</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(league.createdAt)}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setConfirmDialog({ open: true, action: "delete-league", leagueId: league.id })}
                                data-testid={`button-delete-league-${league.id}`}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="partidos">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Partidos Pendientes de Aprobación</CardTitle>
                  <CardDescription>
                    Cola unificada: partidos de liga directos y partidos agendados con resultado reportado
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {(() => {
                    const directCount = pendingLeagueMatches?.length ?? 0;
                    const scheduledCount = pendingScheduledMatches?.length ?? 0;
                    if (directCount === 0 && scheduledCount === 0) {
                      return (
                        <div className="text-center py-8">
                          <Check className="h-12 w-12 text-green-500 mx-auto mb-2" />
                          <p className="text-muted-foreground">No hay partidos pendientes de aprobación</p>
                        </div>
                      );
                    }
                    return (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Liga</TableHead>
                              <TableHead>Tipo</TableHead>
                              <TableHead>Equipos / Fecha</TableHead>
                              <TableHead>Resultado</TableHead>
                              <TableHead>Reportado por</TableHead>
                              <TableHead>Acciones</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pendingLeagueMatches?.map((match) => (
                              <TableRow key={`direct-${match.id}`} data-testid={`row-admin-pending-direct-${match.id}`}>
                                <TableCell className="font-medium text-xs">{match.leagueName}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className="text-orange-600 border-orange-300">Directo</Badge>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  {formatDate(match.playedAt)}
                                </TableCell>
                                <TableCell>
                                  <Badge variant="outline">
                                    {Array.isArray(match.score) ? match.score.join(" - ") : "-"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">—</TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-green-600"
                                      onClick={() => matchActionMutation.mutate({ matchId: match.id, matchType: "league", action: "approve" })}
                                      data-testid={`button-approve-league-match-${match.id}`}
                                    >
                                      <Check className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-yellow-600"
                                      onClick={() => matchActionMutation.mutate({ matchId: match.id, matchType: "league", action: "reject" })}
                                      data-testid={`button-reject-league-match-${match.id}`}
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setEditPendingMatch({
                                        id: match.id,
                                        type: "league",
                                        leagueId: match.leagueId,
                                        score: match.score,
                                        winner: match.winner ?? 1,
                                        team1Player1Id: match.team1Player1Id ?? undefined,
                                        team1Player2Id: match.team1Player2Id ?? null,
                                        team2Player1Id: match.team2Player1Id ?? undefined,
                                        team2Player2Id: match.team2Player2Id ?? null,
                                        playedAt: match.playedAt,
                                      })}
                                      data-testid={`button-edit-league-match-${match.id}`}
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="destructive"
                                      size="sm"
                                      onClick={() => setConfirmDialog({ open: true, action: "delete-match", matchId: match.id, matchType: "league" })}
                                      data-testid={`button-delete-league-match-${match.id}`}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                            {pendingScheduledMatches?.map((match) => {
                              const team1 = [match.team1Player1Name, match.team1Player2Name].filter(Boolean).join(" / ") || "—";
                              const team2 = [match.team2Player1Name, match.team2Player2Name].filter(Boolean).join(" / ") || "—";
                              const score = match.score && typeof match.score === "object" && Array.isArray((match.score as any).sets)
                                ? (match.score as any).sets.map((s: any) => `${s.team1}-${s.team2}`).join(", ")
                                : "—";
                              return (
                                <TableRow key={`scheduled-${match.id}`} data-testid={`row-admin-pending-scheduled-${match.id}`}>
                                  <TableCell className="text-xs text-muted-foreground">{match.leagueName ?? "—"}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="text-blue-600 border-blue-300">Agendado</Badge>
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm">
                                      <div>{team1}</div>
                                      <div className="text-muted-foreground text-xs">vs</div>
                                      <div>{team2}</div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Badge variant="outline">{score}</Badge>
                                  </TableCell>
                                  <TableCell className="text-xs text-muted-foreground">
                                    {match.scoreSubmittedByName ?? "—"}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-1">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-green-600"
                                        onClick={() => adminScheduledMatchActionMutation.mutate({ matchId: match.id, action: "approve" })}
                                        disabled={adminScheduledMatchActionMutation.isPending}
                                        data-testid={`button-admin-approve-scheduled-${match.id}`}
                                      >
                                        <Check className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-yellow-600"
                                        onClick={() => adminScheduledMatchActionMutation.mutate({ matchId: match.id, action: "reject" })}
                                        disabled={adminScheduledMatchActionMutation.isPending}
                                        data-testid={`button-admin-reject-scheduled-${match.id}`}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Partidos Globales Pendientes</CardTitle>
                  <CardDescription>
                    Modera partidos globales que requieren aprobación
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {pendingGlobalMatches?.length === 0 ? (
                    <div className="text-center py-8">
                      <Check className="h-12 w-12 text-green-500 mx-auto mb-2" />
                      <p className="text-muted-foreground">No hay partidos pendientes</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>ID</TableHead>
                            <TableHead>Fecha</TableHead>
                            <TableHead>Resultado</TableHead>
                            <TableHead>Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {pendingGlobalMatches?.map((match) => (
                            <TableRow key={match.id}>
                              <TableCell className="font-mono text-xs">{match.id.substring(0, 8)}...</TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDate(match.playedAt)}
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline">
                                  {Array.isArray(match.score) ? match.score.join(" - ") : "-"}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-green-600"
                                    onClick={() => matchActionMutation.mutate({ matchId: match.id, matchType: "global", action: "approve" })}
                                    data-testid={`button-approve-global-match-${match.id}`}
                                  >
                                    <Check className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-yellow-600"
                                    onClick={() => matchActionMutation.mutate({ matchId: match.id, matchType: "global", action: "reject" })}
                                    data-testid={`button-reject-global-match-${match.id}`}
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setEditPendingMatch({
                                      id: match.id,
                                      type: "global",
                                      score: match.score,
                                      winner: match.winner ?? 1,
                                      team1Player1Id: match.team1Player1Id ?? undefined,
                                      team1Player2Id: match.team1Player2Id ?? null,
                                      team2Player1Id: match.team2Player1Id ?? undefined,
                                      team2Player2Id: match.team2Player2Id ?? null,
                                      playedAt: match.playedAt,
                                    })}
                                    data-testid={`button-edit-global-match-${match.id}`}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => setConfirmDialog({ open: true, action: "delete-match", matchId: match.id, matchType: "global" })}
                                    data-testid={`button-delete-global-match-${match.id}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>


              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row gap-4 justify-between">
                    <div>
                      <CardTitle>Todos los Partidos</CardTitle>
                      <CardDescription>
                        Vista completa de todos los partidos (liga y globales)
                      </CardDescription>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Select value={matchStatusFilter} onValueChange={(v) => setMatchStatusFilter(v as "all" | AdminListAllMatchesStatus)}>
                        <SelectTrigger className="w-32" data-testid="select-match-status-filter">
                          <SelectValue placeholder="Estado" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="pending">Pendientes</SelectItem>
                          <SelectItem value="approved">Aprobados</SelectItem>
                          <SelectItem value="rejected">Rechazados</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={matchTypeFilter} onValueChange={(v) => setMatchTypeFilter(v as "all" | AdminListAllMatchesType)}>
                        <SelectTrigger className="w-32" data-testid="select-match-type-filter">
                          <SelectValue placeholder="Tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="league">Liga</SelectItem>
                          <SelectItem value="global">Global</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingAllMatches ? (
                    <div className="text-center py-8">
                      <Activity className="h-8 w-8 text-primary mx-auto mb-2 animate-pulse" />
                      <p className="text-muted-foreground">Cargando partidos...</p>
                    </div>
                  ) : allMatches?.length === 0 ? (
                    <div className="text-center py-8">
                      <Activity className="h-12 w-12 text-muted-foreground/50 mx-auto mb-2" />
                      <p className="text-muted-foreground">No hay partidos</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Jugadores</TableHead>
                            <TableHead>Resultado</TableHead>
                            <TableHead>Estado</TableHead>
                            <TableHead>Fecha</TableHead>
                            <TableHead>Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {allMatches?.map((match) => (
                            <TableRow key={`${match.matchType}-${match.id}`}>
                              <TableCell>
                                <Badge variant={match.matchType === "league" ? "default" : "secondary"}>
                                  {match.matchType === "league" ? "Liga" : "Global"}
                                </Badge>
                                {match.leagueName && (
                                  <div className="text-xs text-muted-foreground mt-1">{match.leagueName}</div>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="text-sm">
                                  <div className={match.winner === 1 ? "font-semibold text-green-600" : ""}>
                                    {match.team1Player1Name || "?"}
                                    {match.team1Player2Name && ` / ${match.team1Player2Name}`}
                                  </div>
                                  <div className="text-muted-foreground">vs</div>
                                  <div className={match.winner === 2 ? "font-semibold text-green-600" : ""}>
                                    {match.team2Player1Name || "?"}
                                    {match.team2Player2Name && ` / ${match.team2Player2Name}`}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="text-sm font-mono">
                                  {match.score?.sets?.map((s, i) => (
                                    <span key={i} className="mr-1">
                                      {s.team1}-{s.team2}
                                    </span>
                                  )) || "-"}
                                </div>
                              </TableCell>
                              <TableCell>
                                {match.status === "pending" && (
                                  <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" />Pendiente</Badge>
                                )}
                                {match.status === "approved" && (
                                  <Badge className="gap-1 bg-green-600"><Check className="h-3 w-3" />Aprobado</Badge>
                                )}
                                {match.status === "rejected" && (
                                  <Badge variant="destructive" className="gap-1"><X className="h-3 w-3" />Rechazado</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">
                                {formatDate(match.playedAt)}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  {match.status === "pending" && (
                                    <>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-green-600"
                                        onClick={() => matchActionMutation.mutate({ matchId: match.id, matchType: match.matchType, action: "approve" })}
                                        data-testid={`button-approve-match-${match.id}`}
                                      >
                                        <Check className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-yellow-600"
                                        onClick={() => matchActionMutation.mutate({ matchId: match.id, matchType: match.matchType, action: "reject" })}
                                        data-testid={`button-reject-match-${match.id}`}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </>
                                  )}
                                  <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => setConfirmDialog({ open: true, action: "delete-match", matchId: match.id, matchType: match.matchType })}
                                    data-testid={`button-delete-match-${match.id}`}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="solicitudes">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row gap-4 justify-between">
                  <div>
                    <CardTitle>Solicitudes de Acceso de Clubes</CardTitle>
                    <CardDescription>
                      Revisa y gestiona las solicitudes de nuevos clubes
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Buscar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-48"
                      data-testid="input-search-requests"
                    />
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-36" data-testid="select-status-filter">
                        <SelectValue placeholder="Estado" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="pending">Pendientes</SelectItem>
                        <SelectItem value="approved">Aprobadas</SelectItem>
                        <SelectItem value="rejected">Rechazadas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingRequests ? (
                  <div className="text-center py-8">
                    <Trophy className="h-8 w-8 text-primary mx-auto mb-2 animate-pulse" />
                    <p className="text-muted-foreground">Cargando solicitudes...</p>
                  </div>
                ) : filteredRequests?.length === 0 ? (
                  <div className="text-center py-8">
                    <ClipboardCheck className="h-12 w-12 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-muted-foreground">No hay solicitudes</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Club</TableHead>
                          <TableHead>Contacto</TableHead>
                          <TableHead>Ubicación</TableHead>
                          <TableHead>Pistas</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRequests?.map((request) => (
                          <TableRow key={request.id}>
                            <TableCell>
                              <div className="font-medium">{request.clubName}</div>
                              <div className="text-xs text-muted-foreground">
                                {request.clubStatus === "operating" && "En operación"}
                                {request.clubStatus === "opening_soon" && "Próxima apertura"}
                                {request.clubStatus === "opening_later" && "Apertura futura"}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">{request.contactName}</div>
                              <div className="text-xs text-muted-foreground">{request.email}</div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm">
                                <MapPin className="h-3 w-3" />
                                {request.city}, {request.country}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {request.numberOfCourts} {request.courtType === "both" ? "(Mix)" : request.courtType === "indoor" ? "(Interior)" : "(Exterior)"}
                              </div>
                            </TableCell>
                            <TableCell>{getStatusBadge(request.status ?? "pending")}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(request.createdAt)}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedRequest(request)}
                                data-testid={`button-view-request-${request.id}`}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                Ver
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="clubes">
            <Card>
              <CardHeader>
                <CardTitle>Directorio de Clubes</CardTitle>
                <CardDescription>Gestiona el directorio público de clubes de pádel.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-3 mb-4">
                  <div className="flex gap-2">
                    <Button
                      variant={clubsTab === "pending" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setClubsTab("pending")}
                      data-testid="filter-clubs-pending"
                    >
                      Pendientes
                    </Button>
                    <Button
                      variant={clubsTab === "verified" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setClubsTab("verified")}
                      data-testid="filter-clubs-verified"
                    >
                      Verificados
                    </Button>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs">País (ISO-2)</Label>
                    <Input
                      value={clubCountry}
                      onChange={(e) => setClubCountry(e.target.value.toUpperCase().slice(0, 2))}
                      className="w-24"
                      data-testid="input-clubs-country"
                    />
                  </div>
                  <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
                    <Label className="text-xs">Buscar</Label>
                    <Input
                      placeholder="Nombre o ciudad"
                      value={clubSearch}
                      onChange={(e) => setClubSearch(e.target.value)}
                      data-testid="input-clubs-search"
                    />
                  </div>
                  <div className="flex gap-2 ml-auto">
                    <Button
                      size="sm"
                      onClick={() => {
                        setCreateClubDraft({ name: "", country: clubCountry || "DO", city: "", verified: true });
                        setCreateClubOpen(true);
                      }}
                      data-testid="button-create-club"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Crear club
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const csv =
                          "name,country,city,verified\n" +
                          'Club Naco,DO,Santo Domingo,true\n' +
                          'Padel Club Piantini,DO,Santo Domingo,true\n' +
                          '"Club, ejemplo con coma",ES,Madrid,false\n';
                        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = "clubes-template.csv";
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      data-testid="button-clubs-template"
                    >
                      <Download className="h-4 w-4 mr-1" /> Template CSV
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setBulkClubsFile(null);
                        setBulkClubsResult(null);
                        setBulkClubsOpen(true);
                      }}
                      data-testid="button-clubs-bulk"
                    >
                      <Upload className="h-4 w-4 mr-1" /> Importar CSV
                    </Button>
                  </div>
                </div>

                {loadingClubs ? (
                  <div className="text-center py-8 text-muted-foreground">Cargando...</div>
                ) : !adminClubs || adminClubs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No hay clubes para mostrar.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                          <th className="py-2 pr-2">Nombre</th>
                          <th className="py-2 pr-2">País</th>
                          <th className="py-2 pr-2">Ciudad</th>
                          <th className="py-2 pr-2">Estado</th>
                          <th className="py-2 pr-2 text-right">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminClubs.map((club) => (
                          <tr key={club.id} className="border-b hover:bg-muted/30" data-testid={`row-club-${club.id}`}>
                            <td className="py-2 pr-2 font-medium">{club.name}</td>
                            <td className="py-2 pr-2">{club.country}</td>
                            <td className="py-2 pr-2">{club.city || "-"}</td>
                            <td className="py-2 pr-2">
                              {club.verified ? (
                                <Badge className="bg-green-600 gap-1"><Check className="h-3 w-3" />Verificado</Badge>
                              ) : (
                                <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" />Pendiente</Badge>
                              )}
                            </td>
                            <td className="py-2 pr-2">
                              <div className="flex justify-end gap-1">
                                {!club.verified && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => updateClubMutation.mutate({ id: club.id, data: { verified: true } })}
                                    data-testid={`button-verify-club-${club.id}`}
                                  >
                                    <Check className="h-4 w-4" />
                                  </Button>
                                )}
                                {club.verified && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => updateClubMutation.mutate({ id: club.id, data: { verified: false } })}
                                    data-testid={`button-unverify-club-${club.id}`}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingClub(club);
                                    setClubEditDraft({ name: club.name, country: club.country, city: club.city || "" });
                                  }}
                                  data-testid={`button-edit-club-${club.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => { setMergeClub(club); setMergeTargetId(""); }}
                                  data-testid={`button-merge-club-${club.id}`}
                                >
                                  <GitMerge className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => setDeleteClubId(club.id)}
                                  data-testid={`button-delete-club-${club.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Dialog open={!!editingClub} onOpenChange={(o) => !o && setEditingClub(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Editar club</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Nombre</Label>
                    <Input
                      value={clubEditDraft.name}
                      onChange={(e) => setClubEditDraft((d) => ({ ...d, name: e.target.value }))}
                      data-testid="input-edit-club-name"
                    />
                  </div>
                  <div>
                    <Label>País (ISO-2)</Label>
                    <Input
                      value={clubEditDraft.country}
                      onChange={(e) => setClubEditDraft((d) => ({ ...d, country: e.target.value.toUpperCase().slice(0, 2) }))}
                      data-testid="input-edit-club-country"
                    />
                  </div>
                  <div>
                    <Label>Ciudad</Label>
                    <Input
                      value={clubEditDraft.city}
                      onChange={(e) => setClubEditDraft((d) => ({ ...d, city: e.target.value }))}
                      data-testid="input-edit-club-city"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setEditingClub(null)}>Cancelar</Button>
                  <Button
                    onClick={() => {
                      if (!editingClub) return;
                      updateClubMutation.mutate({
                        id: editingClub.id,
                        data: {
                          name: clubEditDraft.name,
                          country: clubEditDraft.country,
                          city: clubEditDraft.city.trim() ? clubEditDraft.city.trim() : null,
                        },
                      });
                    }}
                    disabled={updateClubMutation.isPending}
                    data-testid="button-save-club"
                  >
                    Guardar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={!!mergeClub} onOpenChange={(o) => !o && setMergeClub(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Fusionar club</DialogTitle>
                  <DialogDescription>
                    Reasigna todos los datos de "{mergeClub?.name}" al club destino y elimina el origen.
                  </DialogDescription>
                </DialogHeader>
                <div>
                  <Label>ID del club destino</Label>
                  <Input
                    value={mergeTargetId}
                    onChange={(e) => setMergeTargetId(e.target.value)}
                    placeholder="club_..."
                    data-testid="input-merge-target"
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setMergeClub(null)}>Cancelar</Button>
                  <Button
                    onClick={() => {
                      if (!mergeClub || !mergeTargetId.trim()) return;
                      mergeClubMutation.mutate({ sourceId: mergeClub.id, targetId: mergeTargetId.trim() });
                    }}
                    disabled={mergeClubMutation.isPending || !mergeTargetId.trim()}
                    data-testid="button-confirm-merge"
                  >
                    Fusionar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={createClubOpen} onOpenChange={setCreateClubOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Crear club</DialogTitle>
                  <DialogDescription>
                    Añade un nuevo club al directorio público. Se marca como verificado por defecto.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Nombre</Label>
                    <Input
                      value={createClubDraft.name}
                      onChange={(e) => setCreateClubDraft((d) => ({ ...d, name: e.target.value }))}
                      data-testid="input-create-club-name"
                    />
                  </div>
                  <div>
                    <Label>País (ISO-2)</Label>
                    <Input
                      value={createClubDraft.country}
                      onChange={(e) => setCreateClubDraft((d) => ({ ...d, country: e.target.value.toUpperCase().slice(0, 2) }))}
                      data-testid="input-create-club-country"
                    />
                  </div>
                  <div>
                    <Label>Ciudad (opcional)</Label>
                    <Input
                      value={createClubDraft.city}
                      onChange={(e) => setCreateClubDraft((d) => ({ ...d, city: e.target.value }))}
                      data-testid="input-create-club-city"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={createClubDraft.verified}
                      onChange={(e) => setCreateClubDraft((d) => ({ ...d, verified: e.target.checked }))}
                      data-testid="input-create-club-verified"
                    />
                    Marcar como verificado
                  </label>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateClubOpen(false)}>Cancelar</Button>
                  <Button
                    onClick={() => {
                      const name = createClubDraft.name.trim();
                      const country = createClubDraft.country.trim().toUpperCase();
                      if (name.length < 2 || country.length !== 2) {
                        toast({ title: "Datos incompletos", description: "Nombre (≥2) y país ISO-2 son obligatorios.", variant: "destructive" });
                        return;
                      }
                      createClubMutation.mutate({
                        name,
                        country,
                        city: createClubDraft.city.trim() ? createClubDraft.city.trim() : null,
                        verified: createClubDraft.verified,
                      });
                    }}
                    disabled={createClubMutation.isPending}
                    data-testid="button-confirm-create-club"
                  >
                    Crear
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={bulkClubsOpen} onOpenChange={(o) => { setBulkClubsOpen(o); if (!o) { setBulkClubsFile(null); setBulkClubsResult(null); } }}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Importar clubes desde CSV</DialogTitle>
                  <DialogDescription>
                    Sube un CSV con columnas <code>name,country,city,verified</code>. Descarga el template si necesitas la estructura exacta. Los duplicados (mismo nombre/país/ciudad) se omiten automáticamente.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      setBulkClubsFile(e.target.files?.[0] ?? null);
                      setBulkClubsResult(null);
                    }}
                    data-testid="input-bulk-clubs-file"
                  />
                  {bulkClubsResult && (
                    <div className="rounded border p-3 text-sm space-y-1">
                      {typeof bulkClubsResult.csvDataRows === "number" && (
                        <div className="text-muted-foreground">Filas en el CSV: <strong>{bulkClubsResult.csvDataRows}</strong></div>
                      )}
                      <div>Filas enviadas al servidor: <strong>{bulkClubsResult.total}</strong></div>
                      <div className="text-green-600">Insertados: <strong>{bulkClubsResult.inserted}</strong></div>
                      <div className="text-muted-foreground">Omitidos (duplicados): <strong>{bulkClubsResult.skipped}</strong></div>
                      {bulkClubsResult.invalidRows && bulkClubsResult.invalidRows.length > 0 && (
                        <div className="text-amber-600">
                          <div>Descartadas en cliente: <strong>{bulkClubsResult.invalidRows.length}</strong></div>
                          <ul className="list-disc list-inside max-h-24 overflow-auto text-xs">
                            {bulkClubsResult.invalidRows.slice(0, 20).map((err, i) => (
                              <li key={i}>Línea {err.line}: {err.reason}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {bulkClubsResult.errors?.length > 0 && (
                        <div className="text-destructive">
                          <div>Errores en servidor: <strong>{bulkClubsResult.errors.length}</strong></div>
                          <ul className="list-disc list-inside max-h-32 overflow-auto text-xs">
                            {bulkClubsResult.errors.slice(0, 20).map((err, i) => (
                              <li key={i}>Fila {err.row}: {err.message}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBulkClubsOpen(false)}>Cerrar</Button>
                  <Button
                    onClick={async () => {
                      if (!bulkClubsFile) {
                        toast({ title: "Selecciona un archivo", variant: "destructive" });
                        return;
                      }
                      try {
                        const text = await bulkClubsFile.text();
                        const { items, invalidRows, totalDataRows } = parseClubsCsv(text);
                        if (items.length === 0) {
                          setBulkClubsResult({ inserted: 0, skipped: 0, total: 0, errors: [], invalidRows, csvDataRows: totalDataRows });
                          toast({ title: "CSV sin filas válidas", description: invalidRows.length ? `${invalidRows.length} fila(s) descartadas.` : undefined, variant: "destructive" });
                          return;
                        }
                        bulkClubsMutation.mutate(items, {
                          onSuccess: (result: any) => {
                            setBulkClubsResult({ ...result, invalidRows, csvDataRows: totalDataRows });
                          },
                        });
                      } catch (e: any) {
                        toast({ title: "Error leyendo CSV", description: e?.message || String(e), variant: "destructive" });
                      }
                    }}
                    disabled={bulkClubsMutation.isPending || !bulkClubsFile}
                    data-testid="button-confirm-bulk-clubs"
                  >
                    {bulkClubsMutation.isPending ? "Cargando..." : "Importar"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteClubId} onOpenChange={(o) => !o && setDeleteClubId(null)}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar club?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta acción no se puede deshacer. Los registros que lo referencian quedarán sin club asignado.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteClubId && deleteClubMutation.mutate(deleteClubId)}
                    data-testid="button-confirm-delete-club"
                  >
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </TabsContent>

          <TabsContent value="analiticas">
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <Users className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold">{extendedStats?.totalPlayers ?? 0}</p>
                      <p className="text-xs text-muted-foreground">Total Jugadores</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <Trophy className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold">{extendedStats?.totalLeagues ?? 0}</p>
                      <p className="text-xs text-muted-foreground">Total Ligas</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <Activity className="h-8 w-8 text-green-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold">{extendedStats?.totalMatches ?? 0}</p>
                      <p className="text-xs text-muted-foreground">Total Partidos</p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <TrendingUp className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold">{extendedStats?.activeUsers7Days ?? 0}</p>
                      <p className="text-xs text-muted-foreground">Activos 7 días</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <UserPlus className="h-6 w-6 text-green-500" />
                      <div>
                        <p className="text-xl font-bold">{extendedStats?.newUsersToday ?? 0}</p>
                        <p className="text-xs text-muted-foreground">Nuevos Hoy</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <UserPlus className="h-6 w-6 text-blue-500" />
                      <div>
                        <p className="text-xl font-bold">{extendedStats?.newUsersThisWeek ?? 0}</p>
                        <p className="text-xs text-muted-foreground">Nuevos Esta Semana</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <Ban className="h-6 w-6 text-red-500" />
                      <div>
                        <p className="text-xl font-bold">{extendedStats?.blockedUsers ?? 0}</p>
                        <p className="text-xs text-muted-foreground">Usuarios Bloqueados</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Registro de Auditoría
                  </CardTitle>
                  <CardDescription>
                    Últimas acciones administrativas
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {auditLogs?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">No hay registros de auditoría</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {auditLogs?.slice(0, 20).map((log) => (
                        <div key={log.id} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                          <div>
                            <span className="font-medium text-sm">{log.actionType}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              {log.entityType} {log.entityId ? `(${log.entityId.substring(0, 8)}...)` : ""}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">{formatDate(log.createdAt)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="configuracion">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Configuración del Sistema
                </CardTitle>
                <CardDescription>
                  Gestiona la configuración global de la plataforma
                </CardDescription>
              </CardHeader>
              <CardContent>
                {systemSettings?.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No hay configuraciones definidas</p>
                ) : (
                  <div className="space-y-4">
                    {systemSettings?.map((setting) => (
                      <div key={setting.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">{setting.key}</p>
                          <p className="text-sm text-muted-foreground">{setting.description || "Sin descripción"}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-sm">{JSON.stringify(setting.value)}</p>
                          <p className="text-xs text-muted-foreground">
                            Actualizado: {formatDate(setting.updatedAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6 p-4 border rounded-lg bg-muted/50">
                  <p className="font-medium mb-2">Acciones Rápidas</p>
                  <div className="flex gap-2 flex-wrap">
                    <Button variant="outline" onClick={() => handleExport("users")} data-testid="button-quick-export-users">
                      <Download className="h-4 w-4 mr-2" />
                      Exportar Usuarios
                    </Button>
                    <Button variant="outline" onClick={() => handleExport("leagues")} data-testid="button-quick-export-leagues">
                      <Download className="h-4 w-4 mr-2" />
                      Exportar Ligas
                    </Button>
                  </div>
                </div>

                <div className="mt-6 p-4 border rounded-lg bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-900">
                  <p className="font-medium mb-2 text-orange-700 dark:text-orange-400">Herramientas de Recálculo</p>
                  <p className="text-sm text-muted-foreground mb-3">Usa estas herramientas para corregir inconsistencias en niveles o ratings.</p>
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      variant="outline" 
                      onClick={handleRebuildLevelHistory} 
                      disabled={isRebuildingLevels}
                      data-testid="button-rebuild-level-history"
                      className="border-orange-300 hover:bg-orange-100 dark:border-orange-800 dark:hover:bg-orange-900/30"
                    >
                      <RotateCcw className={`h-4 w-4 mr-2 ${isRebuildingLevels ? 'animate-spin' : ''}`} />
                      {isRebuildingLevels ? 'Reconstruyendo...' : 'Reconstruir Historial de Niveles'}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleRecalculateRatings} 
                      disabled={isRecalculatingRatings}
                      data-testid="button-recalculate-ratings"
                      className="border-orange-300 hover:bg-orange-100 dark:border-orange-800 dark:hover:bg-orange-900/30"
                    >
                      <TrendingUp className={`h-4 w-4 mr-2 ${isRecalculatingRatings ? 'animate-spin' : ''}`} />
                      {isRecalculatingRatings ? 'Recalculando...' : 'Recalcular ELO de Ligas'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleRecalculateNationalRanks}
                      disabled={isRecalculatingNationalRanks}
                      data-testid="button-recalculate-national-ranks"
                      className="border-orange-300 hover:bg-orange-100 dark:border-orange-800 dark:hover:bg-orange-900/30"
                    >
                      <Globe className={`h-4 w-4 mr-2 ${isRecalculatingNationalRanks ? 'animate-spin' : ''}`} />
                      {isRecalculatingNationalRanks ? 'Recalculando...' : 'Recalcular Ranking Nacional'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalles de la Solicitud</DialogTitle>
            <DialogDescription>
              Solicitud de acceso para club
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Nombre del Club</p>
                  <p className="font-medium">{selectedRequest.clubName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado Actual</p>
                  {getStatusBadge(selectedRequest.status ?? "pending")}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Mail className="h-3 w-3" /> Email
                  </p>
                  <p className="font-medium">{selectedRequest.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Phone className="h-3 w-3" /> Teléfono
                  </p>
                  <p className="font-medium">{selectedRequest.phone || "-"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Nombre de Contacto</p>
                  <p className="font-medium">{selectedRequest.contactName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> Ubicación
                  </p>
                  <p className="font-medium">{selectedRequest.city}, {selectedRequest.country}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Pistas</p>
                  <p className="font-medium">
                    {selectedRequest.numberOfCourts} pistas
                    ({selectedRequest.courtType === "both" ? "Interior/Exterior" : 
                      selectedRequest.courtType === "indoor" ? "Interior" : "Exterior"})
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado del Club</p>
                  <p className="font-medium">
                    {selectedRequest.clubStatus === "operating" && "En operación"}
                    {selectedRequest.clubStatus === "opening_soon" && "Próxima apertura"}
                    {selectedRequest.clubStatus === "opening_later" && "Apertura futura"}
                  </p>
                </div>
              </div>

              {selectedRequest.message && (
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" /> Mensaje
                  </p>
                  <p className="text-sm bg-muted p-3 rounded-lg mt-1">{selectedRequest.message}</p>
                </div>
              )}

              {selectedRequest.status === "pending" && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Notas de Revisión (opcional)</p>
                  <Textarea
                    placeholder="Agregar notas sobre esta solicitud..."
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    className="mb-4"
                    data-testid="textarea-review-notes"
                  />
                </div>
              )}

              {selectedRequest.reviewNotes && selectedRequest.status !== "pending" && (
                <div>
                  <p className="text-sm text-muted-foreground">Notas de Revisión</p>
                  <p className="text-sm bg-muted p-3 rounded-lg mt-1">{selectedRequest.reviewNotes}</p>
                </div>
              )}
            </div>
          )}

          {selectedRequest?.status === "pending" && (
            <DialogFooter className="gap-2">
              <Button
                variant="destructive"
                onClick={() => updateRequestMutation.mutate({ 
                  id: selectedRequest.id, 
                  status: "rejected",
                  notes: reviewNotes 
                })}
                disabled={updateRequestMutation.isPending}
                data-testid="button-reject-request"
              >
                <X className="h-4 w-4 mr-2" />
                Rechazar
              </Button>
              <Button
                onClick={() => updateRequestMutation.mutate({ 
                  id: selectedRequest.id, 
                  status: "approved",
                  notes: reviewNotes 
                })}
                disabled={updateRequestMutation.isPending}
                data-testid="button-approve-request"
              >
                <Check className="h-4 w-4 mr-2" />
                Aprobar
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Acción</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog.action === "block" && "¿Estás seguro de que quieres bloquear este usuario?"}
              {confirmDialog.action === "unblock" && "¿Estás seguro de que quieres desbloquear este usuario?"}
              {confirmDialog.action === "make-admin" && "¿Estás seguro de que quieres dar permisos de admin a este usuario?"}
              {confirmDialog.action === "remove-admin" && "¿Estás seguro de que quieres quitar los permisos de admin?"}
              {confirmDialog.action === "reset-stats" && "¿Estás seguro de que quieres reiniciar las estadísticas de este usuario?"}
              {confirmDialog.action === "delete-league" && "¿Estás seguro de que quieres eliminar esta liga? Esta acción no se puede deshacer."}
              {confirmDialog.action === "delete-match" && "¿Estás seguro de que quieres eliminar este partido? Esta acción no se puede deshacer."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-confirm">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmDialog.userId && ["block", "unblock", "make-admin", "remove-admin", "reset-stats"].includes(confirmDialog.action)) {
                  userActionMutation.mutate({ userId: confirmDialog.userId, action: confirmDialog.action });
                } else if (confirmDialog.leagueId && confirmDialog.action === "delete-league") {
                  deleteLeagueMutation.mutate(confirmDialog.leagueId);
                } else if (confirmDialog.matchId && confirmDialog.matchType && confirmDialog.action === "delete-match") {
                  matchActionMutation.mutate({ matchId: confirmDialog.matchId, matchType: confirmDialog.matchType, action: "delete" });
                }
              }}
              data-testid="button-confirm-action"
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditPendingMatchDialog
        open={!!editPendingMatch}
        onOpenChange={(open) => {
          if (!open) setEditPendingMatch(null);
        }}
        match={editPendingMatch}
      />

      <Dialog open={notificationDialog} onOpenChange={setNotificationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar Notificación</DialogTitle>
            <DialogDescription>
              Envía una notificación manual a un usuario
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Título</label>
              <Input
                value={notificationData.title}
                onChange={(e) => setNotificationData({ ...notificationData, title: e.target.value })}
                placeholder="Título de la notificación"
                data-testid="input-notification-title"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Mensaje</label>
              <Textarea
                value={notificationData.message}
                onChange={(e) => setNotificationData({ ...notificationData, message: e.target.value })}
                placeholder="Contenido del mensaje"
                data-testid="textarea-notification-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotificationDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => sendNotificationMutation.mutate(notificationData)}
              disabled={!notificationData.title || !notificationData.message || sendNotificationMutation.isPending}
              data-testid="button-send-notification"
            >
              <Bell className="h-4 w-4 mr-2" />
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
